import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Loader2, MailX, RotateCw } from 'lucide-react'

import { Button } from 'components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card'
import { Switch } from 'components/ui/switch'
import { toast } from 'components/ui/toast-store'
import { cn } from 'lib/utils'

import { getEmailPreferences, updateEmailPreferences } from '../services/notificationPreferencesService'
import type { EmailPreferences, EmailPreferencesUpdate } from '../types/notification.types'

/** The anchor notification emails link to: /admin/userprofile#notifications. */
export const NOTIFICATIONS_ANCHOR = 'notifications'

const PAUSE_KEY = '__paused'

const messageOf = (cause: any, fallback: string) => cause?.response?.data?.message ?? fallback

/** The types, grouped by category, in the order the server lists them. */
const byCategory = (types: EmailPreferences['types']) => {
	const groups = new Map<string, EmailPreferences['types']>()
	for (const type of types) groups.set(type.category, [...(groups.get(type.category) ?? []), type])
	return [...groups.entries()]
}

/** Applies a change locally, for the optimistic update. */
const applied = (prefs: EmailPreferences, update: EmailPreferencesUpdate): EmailPreferences => ({
	paused: update.paused ?? prefs.paused,
	types: prefs.types.map(type => (update.email && type.type in update.email ? { ...type, email: update.email[type.type] } : type)),
})

export interface EmailPreferencesSectionProps {
	className?: string
}

/**
 * Which notification emails the user receives (MYD-543).
 *
 * The switches come from the server's catalog, so a new notification type
 * appears here without a frontend change. Each switch saves on its own, with
 * no Save button: it is applied at once, and put back with an error if the
 * server refuses. It is deliberately separate from the profile form, whose
 * Update button never touches these settings.
 *
 * Email only: every notification still appears in the app.
 */
export function EmailPreferencesSection({ className }: EmailPreferencesSectionProps) {
	const isImpersonating = useSelector((state: any) => Boolean(state.userLogin?.userInfo?.isImpersonating))
	const [prefs, setPrefs] = useState<EmailPreferences | null>(null)
	const [loadError, setLoadError] = useState<string | null>(null)
	const [saving, setSaving] = useState<Set<string>>(new Set())
	const sectionRef = useRef<HTMLDivElement>(null)

	const load = useCallback(async () => {
		setLoadError(null)
		try {
			setPrefs(await getEmailPreferences())
		} catch (cause) {
			setLoadError(messageOf(cause, 'Your email settings could not be loaded.'))
		}
	}, [])

	useEffect(() => {
		load()
	}, [load])

	// The "Manage email notifications" link in every email lands here.
	useEffect(() => {
		if (prefs && window.location.hash === `#${NOTIFICATIONS_ANCHOR}`) {
			sectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
		}
	}, [prefs])

	const save = async (key: string, update: EmailPreferencesUpdate) => {
		if (!prefs) return
		const before = prefs
		setPrefs(applied(prefs, update))
		setSaving(current => new Set(current).add(key))
		try {
			setPrefs(await updateEmailPreferences(update))
		} catch (cause) {
			setPrefs(before)
			toast.error(messageOf(cause, 'Please try again.'), { title: 'Email setting not saved' })
		} finally {
			setSaving(current => {
				const next = new Set(current)
				next.delete(key)
				return next
			})
		}
	}

	return (
		<Card id={NOTIFICATIONS_ANCHOR} ref={sectionRef} className={cn('scroll-mt-6', className)}>
			<CardHeader className="pb-3">
				<CardTitle className="text-lg font-semibold">Email notifications</CardTitle>
				<CardDescription>Choose which emails we send you. You will still see every notification in the app.</CardDescription>
			</CardHeader>

			<CardContent className="space-y-5">
				{loadError ? (
					<div className="flex flex-wrap items-center gap-3 text-sm text-destructive" role="alert">
						{loadError}
						<Button variant="ghost" isOutline onClick={load}>
							<RotateCw className="mr-1.5 h-3.5 w-3.5" />
							Try again
						</Button>
					</div>
				) : !prefs ? (
					<p className="flex items-center gap-2 text-sm text-gray-500">
						<Loader2 className="h-4 w-4 animate-spin" />
						Loading your email settings…
					</p>
				) : (
					<>
						{isImpersonating && (
							<p className="rounded-md bg-warning/15 px-3 py-2 text-sm text-gray-800">
								Support sessions are read-only, so these settings cannot be changed here.
							</p>
						)}

						<SettingRow
							label="Pause all optional emails"
							description="Stops every email below until you turn it back on. Your choices are kept."
							checked={prefs.paused}
							disabled={isImpersonating || saving.has(PAUSE_KEY)}
							onChange={paused => save(PAUSE_KEY, { paused })}
							icon={prefs.paused ? <MailX className="h-4 w-4 text-warning" /> : null}
						/>

						{byCategory(prefs.types).map(([category, types]) => (
							<section key={category} aria-label={category} className="space-y-3">
								<h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">{category}</h4>
								{types.map(type => (
									<SettingRow
										key={type.type}
										label={type.label}
										description={type.description}
										checked={type.email}
										// Paused emails keep their choice but cannot be changed until resumed.
										disabled={isImpersonating || prefs.paused || saving.has(type.type)}
										dimmed={prefs.paused}
										onChange={email => save(type.type, { email: { [type.type]: email } })}
									/>
								))}
							</section>
						))}

						<p className="border-t border-gray-100 pt-4 text-xs text-gray-500">
							Security and account emails, such as sign-in codes and password resets, are always sent.
						</p>
					</>
				)}
			</CardContent>
		</Card>
	)
}

interface SettingRowProps {
	label: string
	description?: string
	checked: boolean
	disabled?: boolean
	dimmed?: boolean
	icon?: React.ReactNode
	onChange: (checked: boolean) => void
}

function SettingRow({ label, description, checked, disabled, dimmed, icon, onChange }: SettingRowProps) {
	return (
		<div className={cn('flex items-start justify-between gap-4', dimmed && 'opacity-60')}>
			<div className="min-w-0">
				<p className="flex items-center gap-1.5 text-sm font-medium text-gray-900">
					{icon}
					{label}
				</p>
				{description && <p className="text-xs text-gray-500">{description}</p>}
			</div>
			<Switch aria-label={label} checked={checked} disabled={disabled} onChange={event => onChange(event.target.checked)} />
		</div>
	)
}

export default EmailPreferencesSection
